import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SharedDrawer from '../Shared/SharedDrawer';
import { mapProjectsToJobById } from '../../utils/mapProjectsToJobById';
import { getCompanySlug, getProjectSlug } from '../../utils/slugUtils';

const TeamDisplay = ({ size }) => {
  const interviewTheme = {
    primary: '#6366f1',
    secondary: '#8b5cf6',
    accent: '#ec4899',
  };
  
  return (
    <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
      {Array.from({ length: parseInt(size, 10) }).map((_, index) => (
        <Box
          key={index}
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${interviewTheme.primary} 0%, ${interviewTheme.secondary} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 4px rgba(99, 102, 241, 0.2)',
          }}
        >
          <PersonIcon
            fontSize="small"
            sx={{ color: 'white' }}
          />
        </Box>
      ))}
    </Box>
  );
};

const JobModal = ({ jobId, open, onClose, initialProject, job: jobProp }) => {
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    // Update URL back to home if we're on a company/project URL
    if (initialProject || (job && window.location.pathname.startsWith('/company/'))) {
      window.history.pushState({}, '', '/');
    }
    onClose();
  };

  useEffect(() => {
    const fetchJobData = async () => {
      if (open && jobProp) {
        // If job prop is provided (from URL), use it directly - it already has projects
        setJob(jobProp);
        setLoading(false);
      } else if (open && jobId) {
        // Only fetch if job prop is not provided (clicked from page)
        setLoading(true);
        try {
          const jobData = await mapProjectsToJobById(jobId);
          setJob(jobData);
        } catch (error) {
          console.error('Failed to load job data:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchJobData();

    if (!open) {
      setJob(null);
      setSelectedProject(null);
      setLoading(false);
    }
  }, [open, jobId, jobProp]);

  // Handle initialProject selection after job is loaded
  useEffect(() => {
    if (job && initialProject && job.projects) {
      const project = job.projects.find(
        p => getProjectSlug(p) === getProjectSlug(initialProject)
      );
      if (project) {
        setSelectedProject(project);
      }
    }
  }, [job, initialProject]);

  if (!open || (!job && !loading)) return null;
  
  // Show loading state if job is being fetched
  if (loading || !job) {
    return (
      <SharedDrawer
        open={open}
        onClose={handleClose}
        title="Loading..."
        content=""
      />
    );
  }

  const isSingleProject = job.projects && job.projects.length === 1;
  
  // If no single project, show about text or empty string
  const defaultContent = job.about || '';

  // Handle project navigation with URL update
  const handleProjectClick = (project) => {
    const companySlug = getCompanySlug(job.company);
    const projectSlug = getProjectSlug(project);
    // Update URL without navigation
    window.history.pushState({}, '', `/company/${companySlug}/project/${projectSlug}`);
    setSelectedProject(project);
  };

  // If a project is selected (from URL), show its content directly
  const displayContent = selectedProject 
    ? selectedProject.content 
    : (isSingleProject ? job.projects[0].content : defaultContent);

  // If a project is selected, don't show navigation (show project content directly)
  const shouldShowNavigation = !selectedProject && !isSingleProject && job.projects && job.projects.length > 0;

  return (
    <SharedDrawer
      open={open}
      onClose={handleClose}
      title={selectedProject ? selectedProject.title : job.company}
      content={displayContent}
      about={job.about}
      mission={job.mission}
      team={job.team_size && parseInt(job.team_size, 10) > 0 && <TeamDisplay size={job.team_size} />}
      navigationContent={
        shouldShowNavigation
          ? {
              title: 'Projects',
              items: job.projects.map((project) => ({
                title: project.title,
                content: project.content,
                onClick: () => handleProjectClick(project),
              })),
            }
          : null
      }
    />
  );
};

export default JobModal;